import React, { useState } from 'react';
import { theme } from '../../../style/global-style';
import { numberFormat } from '../../../utils/formatting';
import { getDetectedDevice } from '../../../utils/device-detection';
import { DreamlinesButton } from '../../atoms/dreamlines-button/dreamlines-button';
import { IconTextbox } from '../../atoms/icon-textbox/icon-textbox';
import { NextImage } from '../../atoms/image/image';
import { ILabelIconProps, LabelIcon } from '../../atoms/label-icon/label-icon';
import { SectionHeadline } from '../../atoms/section-headline/section-headline';
import { SimpleGallery } from '../../atoms/simple-gallery/simple-gallery';
import { LightboxGallery } from '../../atoms/lightbox-gallery/lightbox-gallery';
import { ISuggestionProps, SuggestionBox } from '../../atoms/suggestion-box/suggestion-box';
import { TabContainer } from '../../atoms/tab-container/tab-container';
import { Modal } from '../../molecules/modal/modal';
import {
    StyledAspectRatio,
    StyledButtonContainer,
    StyledContentWrapper,
    StyledGalleryContainer,
    StyledHighlightsList,
    StyledImageWrapper,
    StyledInnerWrapper,
    StyledLabelIconContainer,
    StyledLabelIconList,
    StyledLabelIconItem,
    StyledLabelIconModalContainer,
    StyledOuterWrapper,
    StyledRow,
    StyledSubHeadline,
    StyledSuggestionBoxContainer,
    StyledEnlargedIcon,
    StyledText,
    StyledHighlightWrapper,
} from './overview.style';
import { __ } from '../../../utils/translation-service';
import Lightbox from 'yet-another-react-lightbox';
import 'yet-another-react-lightbox/styles.css';
import Thumbnails from 'yet-another-react-lightbox/plugins/thumbnails';
import 'yet-another-react-lightbox/plugins/thumbnails.css';

export type IExtendedSuggestionBox = ISuggestionProps & { showOnMobile?: boolean };
export interface IImage {
    src: string;
    alt: string;
    placeholder: string;
    width: number;
    height: number;
}

interface IProps {
    title: string;
    headline: string;
    subHeadline1: string;
    buttonLabel: string;
    descriptionPoints: string[] | null;
    icons: ILabelIconProps[];
    boxes?: IExtendedSuggestionBox[] | null;
    images: IImage[];
    highlights: React.ReactElement[];
    description?: string;
}

export const Overview = ({
    title,
    headline,
    subHeadline1,
    buttonLabel,
    descriptionPoints,
    icons,
    boxes,
    images,
    highlights,
    description,
}: IProps) => {
    const [isOpen, setIsOpen] = React.useState(false);
    const handleOpenModal = () => {
        setIsOpen(true);
    };
    const handleCloseModal = () => {
        setIsOpen(false);
    };

    const isMobile = getDetectedDevice() === 'mobile';

    const [index, setIndex] = useState(-1);

    const slides = images.map((image) => {
        return {
            src: image.src,
            alt: image.alt,
            placeholder: image.placeholder,
        };
    });

    return (
        <StyledOuterWrapper data-testid={'overview-component'}>
            <SectionHeadline firstChunk={title} secondChunk={headline} />
            <StyledLabelIconContainer>
                <StyledLabelIconList>
                    {icons.map((icon) => {
                        const amount =
                            typeof icon.amount === 'number' &&
                            icon.type !== 'buildYear' &&
                            icon.type !== 'newestShip'
                                ? numberFormat(icon.amount)
                                : icon.amount;
                        return (
                            <StyledLabelIconItem key={icon.type + icon.amount}>
                                <LabelIcon
                                    key={icon.type}
                                    type={icon.type}
                                    amount={amount}
                                    iconColor={theme.shades.white}
                                    fontColor={theme.shades.white}
                                />
                            </StyledLabelIconItem>
                        );
                    })}
                </StyledLabelIconList>
            </StyledLabelIconContainer>
            <StyledInnerWrapper>
                <StyledHighlightsList>
                    <StyledHighlightWrapper>{highlights}</StyledHighlightWrapper>
                </StyledHighlightsList>
            </StyledInnerWrapper>
            <StyledInnerWrapper>
                <StyledRow>
                    <StyledGalleryContainer>
                        {isMobile ? (
                            <SimpleGallery showShadow>
                                {images.map((image, index) => (
                                    <StyledImageWrapper key={`${image}-${index}`}>
                                        <StyledAspectRatio>
                                            <StyledContentWrapper>
                                                <NextImage
                                                    alt={image.alt}
                                                    imgUrl={image.src}
                                                    width={image.width}
                                                    height={image.height}
                                                    placeholder={image.placeholder}
                                                    containerHeight="100%"
                                                />
                                            </StyledContentWrapper>
                                            <DreamlinesButton
                                                excludeArrow
                                                disabled
                                                tertiary
                                                style={{
                                                    borderColor: 'transparent',
                                                    color: theme.shades.white,
                                                    marginTop: '-40px',
                                                    position: 'absolute',
                                                    right: '5px',
                                                }}
                                                onClick={() => setIndex(index)}
                                            >
                                                <StyledEnlargedIcon />
                                            </DreamlinesButton>
                                        </StyledAspectRatio>
                                    </StyledImageWrapper>
                                ))}
                            </SimpleGallery>
                        ) : (
                            <LightboxGallery images={images}></LightboxGallery>
                        )}
                    </StyledGalleryContainer>
                    <Lightbox
                        open={index >= 0}
                        index={index}
                        close={() => setIndex(-1)}
                        slides={slides}
                        plugins={[Thumbnails]}
                    ></Lightbox>
                </StyledRow>

                {description && (
                    <StyledRow>
                        <StyledText>
                            <StyledSubHeadline>{subHeadline1}</StyledSubHeadline>
                            <div dangerouslySetInnerHTML={{ __html: description }} />
                        </StyledText>
                    </StyledRow>
                )}

                <StyledRow>
                    {boxes?.map((box) => {
                        return (
                            <StyledSuggestionBoxContainer
                                key={box.text}
                                showOnMobile={box.showOnMobile}
                            >
                                <SuggestionBox
                                    type={box.type}
                                    headline={box.headline}
                                    text={box.text}
                                />
                            </StyledSuggestionBoxContainer>
                        );
                    })}
                </StyledRow>

                <StyledButtonContainer>
                    <DreamlinesButton
                        style={{
                            color: theme.colors.main4,
                            borderColor: theme.colors.main4,
                            backgroundColor: 'transparent',
                        }}
                        tertiary
                        onClick={handleOpenModal}
                    >
                        {buttonLabel}
                    </DreamlinesButton>
                </StyledButtonContainer>
                <Modal showModal={isOpen} onClose={handleCloseModal} headerText={headline}>
                    <StyledLabelIconModalContainer>
                        <StyledLabelIconList>
                            {icons.map((icon) => (
                                <li key={icon.type + icon.amount}>
                                    <LabelIcon
                                        type={icon.type}
                                        amount={icon.amount}
                                        iconColor={theme.colors.main4}
                                        fontColor={theme.shades.blackGrey}
                                        horizontal
                                    />
                                </li>
                            ))}
                        </StyledLabelIconList>
                    </StyledLabelIconModalContainer>
                    {descriptionPoints && boxes?.length ? (
                        <TabContainer
                            items={[
                                [
                                    __('highlights'),
                                    // eslint-disable-next-line react/jsx-key
                                    <StyledHighlightsList>
                                        {descriptionPoints?.map((item) => (
                                            <li key={item}>{item}</li>
                                        ))}
                                    </StyledHighlightsList>,
                                ],
                                [
                                    __('expertTip'),
                                    <>
                                        {boxes?.map((box) => (
                                            <IconTextbox key={box.text} text={box.text} />
                                        ))}
                                    </>,
                                ],
                            ]}
                        />
                    ) : (
                        descriptionPoints?.length && (
                            <TabContainer
                                items={[
                                    [
                                        __('highlights'),
                                        // eslint-disable-next-line react/jsx-key
                                        <StyledHighlightsList>
                                            {descriptionPoints?.map((item, index) => (
                                                <li key={item + index}>{item}</li>
                                            ))}
                                        </StyledHighlightsList>,
                                    ],
                                ]}
                            />
                        )
                    )}
                </Modal>
            </StyledInnerWrapper>
        </StyledOuterWrapper>
    );
};
